// frontend/modules/nub-user/modules/access-level/hooks/use-access-level.ts
import { useCallback, useEffect, useMemo } from 'react';
import { useAccessLevelStore } from '../stores/access-level-store';
import { useMessage } from '@/shared/hooks/useMessage';
import {
    useGetAllAccessLevels,
    useCreateAccessLevel,
    useUpdateAccessLevel,
    useGetAllPermissions
} from '@/shared/api/client/nub-user-resource';
import { NubAccessLevelDTO, NubPermissionDTO } from '../types';

export const useAccessLevel = () => {
    const store = useAccessLevelStore();
    const message = useMessage();

    // Queries
    const {
        data: accessLevelsResponse,
        isLoading: isLoadingAccessLevels,
        error: accessLevelsError,
        refetch: refetchAccessLevels
    } = useGetAllAccessLevels({
        query: {
            enabled: true,
            staleTime: 5 * 60 * 1000, // 5 minutes
            // @ts-ignore
            cacheTime: 10 * 60 * 1000 // 10 minutes
        }
    });

    const {
        data: permissionsResponse,
        isLoading: isLoadingPermissions
    } = useGetAllPermissions({
        query: {
            enabled: true,
            staleTime: 30 * 60 * 1000, // 30 minutes
            // @ts-ignore
            cacheTime: 60 * 60 * 1000 // 1 hour
        }
    });

    // Mutations
    const createMutation = useCreateAccessLevel();
    const updateMutation = useUpdateAccessLevel();

    // Sync backend data with store
    useEffect(() => {
        if (accessLevelsResponse) {
            store.setAccessLevels(accessLevelsResponse);
        }
    }, [accessLevelsResponse, store.setAccessLevels]);

    useEffect(() => {
        if (permissionsResponse) {
            store.setPermissions(permissionsResponse);
        }
    }, [permissionsResponse, store.setPermissions]);

    // Computed values
    const permissionCategories = useMemo(() => {
        if (!store.permissions.length) return [];
        return [...new Set(store.permissions
            .map(p => p.category)
            .filter(Boolean)
        )].sort();
    }, [store.permissions]);

    const permissionsByCategory = useMemo(() => {
        const map = new Map<string, NubPermissionDTO[]>();
        permissionCategories.forEach(category => {
            map.set(
                // @ts-ignore
                category,
                store.permissions.filter(p => p.category === category)
            );
        });
        return map;
    }, [store.permissions, permissionCategories]);

    // Modal actions
    const handleAccessLevelAction = useCallback((
        accessLevel: NubAccessLevelDTO | null,
        mode: 'view' | 'add' | 'edit' | 'delete'
    ) => {
        store.setActiveAccessLevel(accessLevel);
        store.setActionMode(mode);
        store.setModalOpen(true);

        // Set selected permissions if editing or viewing
        if (accessLevel?.permissions) {
            store.setSelectedPermissions(
                // @ts-ignore
                accessLevel.permissions.map(p => p.code)
            );
        } else {
            store.setSelectedPermissions([]);
        }
    }, [store]);

    const setModalOpen = useCallback((isOpen: boolean) => {
        store.setModalOpen(isOpen);
        if (!isOpen) {
            // Reset state when closing modal
            store.setActiveAccessLevel(null);
            store.setSelectedPermissions([]);
            store.setActionMode('view');
        }
    }, [store]);

    const closeModal = useCallback(() => {
        store.setModalOpen(false);
        store.setActiveAccessLevel(null);
        store.setSelectedPermissions([]);
        store.setActionMode('view');
    }, [store]);

    // Permission management
    const handlePermissionChange = useCallback((permissions: string[]) => {
        store.setSelectedPermissions(permissions);
    }, [store.setSelectedPermissions]);

    // CRUD operations
    const createAccessLevel = useCallback(async (data: NubAccessLevelDTO) => {
        try {
            const response = await createMutation.mutateAsync({ data });
            message.success('Access Level created successfully');
            await refetchAccessLevels();
            closeModal();
            return response.data;
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to create Access Level');
            throw error;
        }
    }, [createMutation, message, refetchAccessLevels, closeModal]);

    const updateAccessLevel = useCallback(async (
        code: string,
        data: NubAccessLevelDTO
    ) => {
        try {
            const response = await updateMutation.mutateAsync({ code, data });
            message.success('Access Level updated successfully');
            await refetchAccessLevels();
            closeModal();
            return response.data;
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to update Access Level');
            throw error;
        }
    }, [updateMutation, message, refetchAccessLevels, closeModal]);

    // Utility functions
    const getPermissionsByCategory = useCallback((category: string) => {
        return permissionsByCategory.get(category) || [];
    }, [permissionsByCategory]);

    const validatePermissions = useCallback((permissions: string[]) => {
        const invalidPermissions = permissions.filter(
            code => !store.permissions.some(p => p.code === code)
        );
        if (invalidPermissions.length > 0) {
            message.error(`Invalid permissions found: ${invalidPermissions.join(', ')}`);
            return false;
        }
        return true;
    }, [store.permissions, message]);

    const findAccessLevelByCode = useCallback((code: string) => {
        return store.accessLevels.find(al => al.code === code);
    }, [store.accessLevels]);

    return {
        // State
        accessLevels: store.accessLevels,
        activeAccessLevel: store.activeAccessLevel,
        permissions: store.permissions,
        selectedPermissions: store.selectedPermissions,
        permissionCategories,
        actionMode: store.actionMode,
        isModalOpen: store.isModalOpen,
        searchTerm: store.searchTerm,
        isLoading: isLoadingAccessLevels || isLoadingPermissions,
        error: accessLevelsError,

        // Modal actions
        handleAccessLevelAction,
        setModalOpen,
        closeModal,

        // Permission management
        handlePermissionChange,
        getPermissionsByCategory,
        validatePermissions,

        // CRUD operations
        createAccessLevel,
        updateAccessLevel,
        refetchAccessLevels,

        // Utility functions
        findAccessLevelByCode,

        // Store actions
        setSearchTerm: store.setSearchTerm,
    };
};